/**
 * Resolve a vendor `image` cell value into a usable <img src>.
 *
 * Sheet authors paste either:
 *   - A Google Drive share link (Right-click → "Get link"). We extract the
 *     file ID and rewrite to `https://lh3.googleusercontent.com/d/<id>=w800`,
 *     which is Google's CDN form: CORS-friendly, fast, served with long-lived
 *     cache headers. Note this URL form is widely used but undocumented; if
 *     Google ever changes it we'd need to migrate to a different rewrite.
 *   - Any direct https:// URL (Squarespace, the vendor's own site, etc.) —
 *     passed through unchanged.
 *
 * Anything else (blank, malformed) returns undefined so the UI falls back
 * to the category-color hero.
 */

const SIZE = 'w800';

const DRIVE_HOSTS = new Set(['drive.google.com', 'docs.google.com']);

const FILE_ID_RE = /^[A-Za-z0-9_-]{10,}$/;

function extractDriveFileId(url: URL): string | null {
  // /file/d/<id>/...  or  /d/<id>/...
  const pathMatch = url.pathname.match(/\/(?:file\/)?d\/([^/]+)/);
  if (pathMatch && FILE_ID_RE.test(pathMatch[1]!)) {
    return pathMatch[1]!;
  }
  // ?id=<id>  (open?id=, uc?id=, thumbnail?id=)
  const idParam = url.searchParams.get('id');
  if (idParam && FILE_ID_RE.test(idParam)) {
    return idParam;
  }
  return null;
}

export function resolveVendorImageUrl(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return undefined;
  }

  // Only https. Plain http would be blocked by the browser's mixed-content
  // policy on Vercel (HTTPS-only) and silently fall back to the glyph; reject
  // it up front so the failure is consistent across environments.
  if (url.protocol !== 'https:') return undefined;

  if (DRIVE_HOSTS.has(url.hostname)) {
    const id = extractDriveFileId(url);
    if (id) return `https://lh3.googleusercontent.com/d/${id}=${SIZE}`;
    // Drive URL we couldn't parse — better to drop than to embed a Drive
    // page URL that won't render in <img>.
    return undefined;
  }

  return url.toString();
}
